import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { format, parseISO } from 'date-fns';
import { 
  Scale, 
  Clock, 
  FileText, 
  Users, 
  Building2,
  RefreshCcw,
  Loader2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

interface DatajudInfoProps {
  datajudData: any;
  lastSync?: string;
  isSyncing: boolean;
  onSync: () => void;
  caseNumber?: string;
}

export const DatajudInfo = ({ 
  datajudData, 
  lastSync, 
  isSyncing, 
  onSync, 
  caseNumber 
}: DatajudInfoProps) => {
  const hasData = datajudData && Object.keys(datajudData).length > 0;
  const movementsCount = datajudData?.movimentacoes?.length || 0;
  const partiesCount = datajudData?.partes?.length || 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Scale className="w-4 h-4" />
          Dados do Datajud
          {hasData && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              Sincronizado
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status da sincronização */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Status:</span>
          <div className="flex items-center gap-2">
            {hasData ? (
              <Badge variant="outline" className="text-green-600">
                <CheckCircle className="w-3 h-3 mr-1" />
                Dados disponíveis
              </Badge>
            ) : (
              <Badge variant="outline" className="text-orange-600">
                <AlertCircle className="w-3 h-3 mr-1" />
                Não sincronizado
              </Badge>
            )}
          </div>
        </div>

        {/* Última sincronização */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Última sincronização:</span>
          <span className="text-sm font-medium">
            {lastSync 
              ? format(parseISO(lastSync), 'dd/MM/yyyy HH:mm')
              : 'Nunca sincronizado'
            }
          </span>
        </div>

        {/* Número do processo */}
        {caseNumber && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Processo:</span>
            <span className="text-sm font-mono">{caseNumber}</span>
          </div>
        )}

        {/* Estatísticas */}
        {hasData && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <FileText className="w-3 h-3" />
                Andamentos:
              </span>
              <span className="text-sm font-medium">{movementsCount}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Users className="w-3 h-3" />
                Partes:
              </span>
              <span className="text-sm font-medium">{partiesCount}</span>
            </div>
            {datajudData?.tribunal && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground flex items-center gap-1">
                  <Building2 className="w-3 h-3" />
                  Tribunal:
                </span>
                <span className="text-sm font-medium">{datajudData.tribunal}</span>
              </div>
            )}
          </div>
        )}

        {/* Botão de sincronização */}
        <Button 
          variant="outline" 
          onClick={onSync} 
          disabled={isSyncing}
          className="w-full"
        >
          {isSyncing ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RefreshCcw className="w-4 h-4 mr-2" />
          )}
          {isSyncing ? 'Sincronizando...' : 'Sincronizar com Datajud'}
        </Button>

        {/* Informação sobre dados reais */}
        {hasData && (
          <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
            <p className="font-medium mb-1">ℹ️ Dados Reais do Tribunal</p>
            <p>Os andamentos exibidos são dados oficiais obtidos diretamente do sistema do tribunal através da API do Datajud.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}; 